import { useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { useDialog } from "../../DialogContext";
import ServiceTimeForm from "../Activities/ActivitiesForms/ServiceTimeForm";
import {
  STOPWATCH_SYNC_EVENT,
  clearStopwatchSession,
  createRunningStopwatchSession,
  formatStopwatchSeconds,
  getStopwatchElapsedSeconds,
  readStopwatchSession,
  writeStopwatchSession,
} from "../../../utils/stopwatchSession";

const Stopwatch = () => {
  const { showDialog } = useDialog();
  const [stopwatchSession, setStopwatchSession] = useState(() =>
    readStopwatchSession(),
  );
  const [nowMs, setNowMs] = useState(() => Date.now());

  const isRunning = Boolean(stopwatchSession?.startedAtMs);
  const elapsedSeconds = isRunning
    ? getStopwatchElapsedSeconds(stopwatchSession, nowMs)
    : 0;

  useEffect(() => {
    const syncStopwatchSession = () => {
      setStopwatchSession(readStopwatchSession());
      setNowMs(Date.now());
    };

    syncStopwatchSession();
    window.addEventListener("storage", syncStopwatchSession);
    window.addEventListener(STOPWATCH_SYNC_EVENT, syncStopwatchSession);

    return () => {
      window.removeEventListener("storage", syncStopwatchSession);
      window.removeEventListener(STOPWATCH_SYNC_EVENT, syncStopwatchSession);
    };
  }, []);

  useEffect(() => {
    if (!isRunning) {
      return;
    }

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 250);

    return () => window.clearInterval(intervalId);
  }, [isRunning]);

  const handleStartStop = () => {
    if (isRunning && stopwatchSession?.startedAtMs) {
      const endTime = new Date();
      const startTime = new Date(stopwatchSession.startedAtMs);

      clearStopwatchSession();
      setStopwatchSession(null);
      setNowMs(Date.now());

      showDialog(
        <ServiceTimeForm
          timeIn={startTime.toISOString()}
          timeOut={endTime.toISOString()}
          elapsedTime={formatStopwatchSeconds(elapsedSeconds)}
        />,
        {
          dialogTitle: "Edit time entry",
        },
      );
      return;
    }

    const nextSession = createRunningStopwatchSession(Date.now());
    writeStopwatchSession(nextSession);
    setStopwatchSession(nextSession);
    setNowMs(Date.now());
  };

  const handleReset = () => {
    if (isRunning) {
      const nextSession = createRunningStopwatchSession(Date.now());
      writeStopwatchSession(nextSession);
      setStopwatchSession(nextSession);
    } else {
      clearStopwatchSession();
      setStopwatchSession(null);
    }

    setNowMs(Date.now());
  };

  return (
    <motion.div
      drag
      dragConstraints={{
        top: 0,
        left: 0,
        right: window.innerWidth - 150,
        bottom: window.innerHeight - 70,
      }}
      style={{
        position: "sticky",
        top: "0rem",
        right: "0rem",
        zIndex: 99,
        cursor: "grab",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "0.5rem",
          borderRadius: "5rem",
          border: "1px solid #FFFFFF2E",
          background: "#FFFFFF40",
          boxShadow: "0 0rem 0.2rem 0 #1F268780",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      >
        <Tooltip
          title={isRunning ? "Pause stopwatch" : "Play stopwatch"}
          placement="bottom"
          arrow
        >
          <IconButton
            sx={{ color: "#fff" }}
            size="small"
            onClick={handleStartStop}
          >
            {isRunning ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
          </IconButton>
        </Tooltip>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            minWidth: "6.5rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#fff",
          }}
        >
          {formatStopwatchSeconds(elapsedSeconds)}
        </Typography>
        <Tooltip title="Reset stopwatch" placement="bottom" arrow>
          <IconButton size="small" sx={{ color: "#fff" }} onClick={handleReset}>
            <RestartAltRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </motion.div>
  );
};

export default Stopwatch;
